import { getCatalogueCardDate } from '../getCatalogueCardDate';
import type { CatalogueCardLike } from '../transformCatalogueEditions/transformCatalogueEditions';
import type { MarkdownTokenList } from '$global/components/Markdown';
import { getImageUrl } from '$global/utils/getImageUrl';

/**
 * A single Primary-section card. Each card represents a whole primary
 * collection rather than one artwork within it.
 */
export interface PrimarySectionCard {
	__typename: 'Primary_Collection';
	id: string;
	name: string;
	/** Markdown: tokens from the server load, or the text. */
	description: string | MarkdownTokenList;
	href: string;
	collection_featured_image: {
		url: string;
		width: number;
		height: number;
	};
	// Used only for ordering; the card itself never renders it.
	releaseDate: string | null;
}

// Newest first. Collections with neither a release_date nor a drop config sort
// last rather than jumping to the top.
const byReleaseDateDesc = (a: PrimarySectionCard, b: PrimarySectionCard) => {
	if (a.releaseDate === b.releaseDate) return 0;
	if (!a.releaseDate) return 1;
	if (!b.releaseDate) return -1;
	return a.releaseDate < b.releaseDate ? 1 : -1;
};

/**
 * Builds the Primary section's cards from the artist's primary collections.
 *
 * A collection is one card — it is never broken down into its artworks. Cards
 * are ordered newest-first, a collection with no release_date falling back to
 * its drop config dates (which Directus cannot sort on, because drop_config is
 * a to-many relation).
 */
export const transformCataloguePrimaryArtworks = (
	primaryCollections: readonly (CatalogueCardLike | null)[] | undefined
): PrimarySectionCard[] =>
	(primaryCollections || [])
		// Without a slug the card has nowhere to link, so the collection is
		// dropped rather than rendered as a dead tile.
		.filter((collection) => collection?.slug)
		.map((collection) => ({
			__typename: 'Primary_Collection' as const,
			id: collection!.id ?? '',
			name: collection!.name || '',
			description:
				collection!.descriptionTokens ?? (collection!.description || ''),
			href: `/primary/collection/${collection!.slug}`,
			collection_featured_image: {
				url: getImageUrl(
					collection!.collection_featured_image?.filename_disk ?? undefined
				),
				width: collection!.collection_featured_image?.width || 0,
				height: collection!.collection_featured_image?.height || 0,
			},
			releaseDate: getCatalogueCardDate(collection!),
		}))
		.sort(byReleaseDateDesc);
